import React, { useCallback, useEffect, useMemo, useState } from 'react';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import { Button, Card, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { graphql, lambda, ml, parse } from 'src/_shared/services/utils.js';
import { updateCompany } from 'src/_shared/api/graphql/custom/index.js';
import AddIntegrationDrawer from './AddIntegrationDrawer.jsx';
import ConnectedIntegrations from './ConnectedIntegrations.jsx';
import ProcessingSettingsCard from './ProcessingSettingsCard.jsx';
import { isWorkato, syncEndpointFor } from './integrationCatalog.js';

const { Text } = Typography;

const EMPTY_SFTP_RULE = { enabled: false, includeKeys: [], excludeKeys: [] };
const EMPTY_ATS_RULE = { enabled: false, schedule: { every: { hours: 0 } } };
const TASKS = ['employee', 'job', 'candidate', 'bonus'];

// The login session caches the company object, so pipeline state flipped since
// login (cutovers happen server-side) would render stale — always re-fetch.
const GET_COMPANY_CONFIGS = `query GetCompanyProcessingConfigs($id: ID!) {
  getCompany(id: $id) {
    id
    SFTPProcessingConfig
    ATSProcessingConfig
  }
}
`;

const seedConfig = (raw, emptyRule) => {
	const parsed = parse(raw) || {};
	const seeded = {};
	for (const task of TASKS) {
		seeded[task] = { ...emptyRule, ...(parsed[task] || {}) };
	}
	return seeded;
};

/**
 * Integrations settings — ATS connections + feed processing. Modular rework of
 * the retired SettingsFormAtsComponent: declarative provider catalog, masked
 * credentials, and a processing section that speaks only the new pipeline.
 */
const IntegrationsSettings = (props) => {
	const {
		allMultiLingualData,
		client,
		currentUser,
		departments,
		managers,
		onCreateDepartment,
		tieredBonuses,
		updateCurrentUserCompany,
	} = props;

	const company = currentUser?.company;

	const [integrations, setIntegrations] = useState([]);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [syncStarted, setSyncStarted] = useState({});

	const [sftpConfig, setSftpConfig] = useState(() =>
		seedConfig(company?.SFTPProcessingConfig, EMPTY_SFTP_RULE)
	);
	const [atsConfig, setAtsConfig] = useState(() =>
		seedConfig(company?.ATSProcessingConfig, EMPTY_ATS_RULE)
	);
	const [baseline, setBaseline] = useState(() =>
		JSON.stringify({
			sftp: seedConfig(company?.SFTPProcessingConfig, EMPTY_SFTP_RULE),
			ats: seedConfig(company?.ATSProcessingConfig, EMPTY_ATS_RULE),
		})
	);
	const [savingConfigs, setSavingConfigs] = useState(false);
	const [saveResult, setSaveResult] = useState(null);
	const [freshCompanyConfig, setFreshCompanyConfig] = useState(null);

	const supportAdmin = useMemo(
		() => parse(currentUser?.admin)?.permissions === '*',
		[currentUser?.admin]
	);

	const dirty = useMemo(
		() => JSON.stringify({ sftp: sftpConfig, ats: atsConfig }) !== baseline,
		[sftpConfig, atsConfig, baseline]
	);

	// Re-seed from the live company record, not the login-cached copy.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const result = await client.query({
					query: gql(GET_COMPANY_CONFIGS),
					variables: { id: company?.id },
					fetchPolicy: 'network-only',
				});
				if (cancelled) return;
				const fresh = result?.data?.getCompany;
				if (!fresh) return;
				setFreshCompanyConfig(fresh);
				const sftp = seedConfig(fresh.SFTPProcessingConfig, EMPTY_SFTP_RULE);
				const ats = seedConfig(fresh.ATSProcessingConfig, EMPTY_ATS_RULE);
				setSftpConfig(sftp);
				setAtsConfig(ats);
				setBaseline(JSON.stringify({ sftp, ats }));
			} catch (error) {
				console.log('Failed to refresh processing configs:', error);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [client, company?.id]);

	const loadIntegrations = useCallback(async () => {
		try {
			const result = await graphql(
				{ input: { id: company?.id }, query: 'getATSSettingsByCompany' },
				'cognito'
			);
			const list = parse(result?.atsIntegration);
			if (Array.isArray(list)) setIntegrations(list);
		} catch (error) {
			console.log('Failed to load ATS settings:', error);
		}
	}, [company?.id]);

	useEffect(() => {
		loadIntegrations();
	}, [loadIntegrations]);

	const persistIntegrations = async (next) => {
		setIntegrations(next);
		await graphql(
			{
				input: { companyId: company?.id, atsIntegration: JSON.stringify(next) },
				update: 'updateATSIntegration',
			},
			'cognito'
		);
	};

	const handleConnect = async ({
		provider,
		fieldValues,
		hiringManager,
		bonusBuilder,
		notificationType,
	}) => {
		setSubmitting(true);
		try {
			const entry = {
				ATSName: provider.value,
				...fieldValues,
				ATSHiringManager: hiringManager,
				ATSBonusBuilder: bonusBuilder || '',
				ATSNotificationType: notificationType,
				// Workato entries historically record the hiring contact as creator.
				ATSCreatedById: isWorkato(provider.value) ? hiringManager?.id : currentUser.id,
			};

			// Single-instance providers update in place; multi-instance providers
			// (dedupeKeys) always append — uniqueness was checked in the drawer.
			const updatable =
				!provider.dedupeKeys && integrations.some((ats) => ats.ATSName === provider.value);
			const next = updatable
				? integrations.map((ats) =>
						ats.ATSName === provider.value ? { ...ats, ...entry } : ats
					)
				: [...integrations, entry];

			await persistIntegrations(next);
			message.success(
				ml('Integration saved.', currentUser, allMultiLingualData)
			);
			return true;
		} catch (error) {
			console.log('Failed to save integration:', error);
			message.error('Could not save the integration — try again.');
			return false;
		} finally {
			setSubmitting(false);
		}
	};

	const handleRemove = async (target) => {
		const identity = (ats) =>
			`${ats.ATSName}|${ats.ATSAPIKey || ''}|${ats.ATSCustomerId || ''}|${ats.ATSPortalId || ''}`;
		try {
			await persistIntegrations(
				integrations.filter((ats) => identity(ats) !== identity(target))
			);
			message.success(ml('Integration removed.', currentUser, allMultiLingualData));
		} catch (error) {
			console.log('Failed to remove integration:', error);
			message.error('Could not remove the integration — try again.');
		}
	};

	const handleSync = (ats) => {
		const endpoint = syncEndpointFor(ats.ATSName);
		if (!endpoint) return;
		lambda({ endpoint, variables: { companyId: currentUser.companyId } });
		setSyncStarted((prev) => ({ ...prev, [ats.ATSName]: true }));
		message.success('Job sync started — allow up to 15 minutes to complete.');
	};

	const handleImportDepartments = (ats) => {
		const apiBoardToken = ats.ATSAPIKey;
		if (!apiBoardToken) {
			message.error('No Greenhouse board token stored on this integration.');
			return;
		}

		fetch(`https://boards-api.greenhouse.io/v1/boards/${apiBoardToken}/departments`)
			.then((res) => res.json())
			.then((result) => {
				const external = result.departments || [];
				let created = 0;
				for (const element of external) {
					const isDuplicate = (departments || []).some(
						(department) => department.name === element.name
					);
					if (!isDuplicate) {
						created += 1;
						onCreateDepartment({
							input: {
								companyId: company.id,
								name: element.name,
								active: true,
								externalDepartmentId: element.id,
							},
						});
					}
				}

				message.success(
					created > 0
						? `Department sync started for ${created} departments — allow 5 minutes.`
						: 'All Greenhouse departments already exist.'
				);
			})
			.catch(() => message.error('Could not reach Greenhouse — check the board token.'));
	};

	const handleSftpRuleChange = (taskType, rule) => {
		setSaveResult(null);
		setSftpConfig((prev) => ({ ...prev, [taskType]: rule }));
	};

	const handleAtsRuleChange = (taskType, rule) => {
		setSaveResult(null);
		setAtsConfig((prev) => ({ ...prev, [taskType]: rule }));
	};

	// Persist the given configs. Shared by the Save button and by activate — which
	// must save its staged live-flip immediately (state updates are async, so we
	// pass the next config explicitly rather than reading it back from state).
	const persistProcessingConfigs = async (sftpToSave, atsToSave) => {
		setSavingConfigs(true);
		setSaveResult(null);
		try {
			// Merge over the freshest stored config so fields this UI doesn't own
			// (dryRunConfirmation, policy additions) survive a concurrent update.
			const source = freshCompanyConfig || company;
			const freshSftp = seedConfig(source?.SFTPProcessingConfig, EMPTY_SFTP_RULE);
			const freshAts = seedConfig(source?.ATSProcessingConfig, EMPTY_ATS_RULE);
			const mergedSftp = {};
			const mergedAts = {};
			for (const task of TASKS) {
				mergedSftp[task] = { ...freshSftp[task], ...sftpToSave[task] };
				mergedAts[task] = { ...freshAts[task], ...atsToSave[task] };
			}

			const result = await client.mutate({
				mutation: gql(updateCompany),
				variables: {
					input: {
						id: company?.id,
						SFTPProcessingConfig: JSON.stringify(mergedSftp),
						ATSProcessingConfig: JSON.stringify(mergedAts),
					},
				},
			});
			updateCurrentUserCompany(result.data.updateCompany);
			setSftpConfig(mergedSftp);
			setAtsConfig(mergedAts);
			setBaseline(JSON.stringify({ sftp: mergedSftp, ats: mergedAts }));
			setSaveResult('success');
		} catch (error) {
			console.log('Error saving processing configs:', error);
			setSaveResult('error');
		} finally {
			setSavingConfigs(false);
		}
	};

	// Save button — ignores the click event, persists current form state.
	const saveProcessingConfigs = () => persistProcessingConfigs(sftpConfig, atsConfig);

	// Activate = flip an SFTP feed to live AND persist in one action (the button
	// used to only stage the change, requiring a second "Save" that was easy to
	// miss — which silently left the feed in dry-run).
	const handleActivateSftp = (taskType, activatedRule) => {
		const nextSftp = { ...sftpConfig, [taskType]: activatedRule };
		setSftpConfig(nextSftp);
		return persistProcessingConfigs(nextSftp, atsConfig);
	};

	return (
		<div>
			<Card
				bordered={false}
				className="setting-card-inner"
				title={
					<div>
						{ml('ATS Integrations', currentUser, allMultiLingualData)}
						<Text type="secondary" style={{ display: 'block', fontSize: 13, fontWeight: 400 }}>
							Connected applicant tracking and HR systems.
						</Text>
					</div>
				}
				extra={
					<Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)}>
						{ml('Connect', currentUser, allMultiLingualData)}
					</Button>
				}
			>
				<ConnectedIntegrations
					integrations={integrations}
					currentUser={currentUser}
					allMultiLingualData={allMultiLingualData}
					onRemove={handleRemove}
					onSync={handleSync}
					syncDisabledFor={(ats) => Boolean(syncStarted[ats.ATSName])}
					onImportDepartments={handleImportDepartments}
				/>
			</Card>

			<ProcessingSettingsCard
				currentUser={currentUser}
				allMultiLingualData={allMultiLingualData}
				sftpConfig={sftpConfig}
				atsConfig={atsConfig}
				onSftpRuleChange={handleSftpRuleChange}
				onAtsRuleChange={handleAtsRuleChange}
				onActivate={handleActivateSftp}
				canActivate={supportAdmin}
				activatedBy={
					[currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') ||
					currentUser?.emailAddress ||
					currentUser?.id
				}
				dirty={dirty}
				saving={savingConfigs}
				onSave={saveProcessingConfigs}
				saveResult={saveResult}
			/>

			<AddIntegrationDrawer
				visible={drawerOpen}
				submitting={submitting}
				currentUser={currentUser}
				allMultiLingualData={allMultiLingualData}
				managers={managers}
				tieredBonuses={tieredBonuses}
				existingIntegrations={integrations}
				onClose={() => setDrawerOpen(false)}
				onSubmit={handleConnect}
			/>
		</div>
	);
};

export default withApollo(IntegrationsSettings);
